/**
 * HR admin — Coverage thresholds (/hr/coverage)
 *
 * Live 1:1 port of the prototype's CoverageThresholds() component at
 * prototype/admin-rules.jsx:315-393.
 *
 * Data: GET /api/v1/teams returns Team[] with slotPct + members.
 * Save: PATCH /api/v1/admin/teams/{id} — wired for all 6 editable columns.
 *
 * Columns:
 *   - slotPct          (absence cap %)     — number input, on-blur PATCH
 *   - floor            (min present count) — number input, on-blur PATCH
 *
 * Note: `overrideApprover` and `criticalRoles` exist on the Team model +
 * admin PATCH but are NOT surfaced in the UI. Per the coverage policy,
 * any exception to the capacity cap requires Director approval (no
 * per-team approver routing), and the policy makes no distinction for
 * "critical roles". The columns were removed to avoid suggesting either
 * lever is configurable today.
 */

import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Team } from '@lms/shared-types'
import { Card } from '../components/primitives/Card'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { useToast } from '../components/primitives/Toast'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { ApiError } from '../api/client'
import { listTeams, updateTeamCoverage, updateTeamThresholds } from '../api/teams'
import { flags } from '../lib/feature-flags'
import { Modal } from '../components/primitives/Modal'
import { Button } from '../components/primitives/Button'
import { Tooltip } from '../components/primitives/Tooltip'
import { listCapacityRequests } from '../api/capacity-requests'
import { activeOverrideFor } from './hr-coverage/capacity-format'

// ─── types ────────────────────────────────────────────────────────────────────

interface RowState {
  /** Controlled value of the absence-cap input (0-100). */
  capInput: string
  /** Controlled value of the floor input (non-negative integer or empty). */
  floorInput: string
  /** Whether the row is mid-save (any field). */
  saving: boolean
  /** Non-null = inline error for this row. */
  error: string | null
  /** Epoch ms of the last successful save for this row; drives the
   *  transient "Saved" chip. Cleared by a setTimeout after ~1.6s. */
  savedAt: number | null
}

type RowMap = Map<string, RowState>

function initialRowFor(team: Team): RowState {
  // Small-team rule: teams of 3 or fewer always enforce a
  // 1-slot floor. Pre-fill the input with 1 when the row hasn't
  // been set yet so HR sees the enforced default.
  const isSmallTeam = team.members <= 3
  const initialFloor =
    team.floor != null ? String(team.floor) : isSmallTeam ? '1' : ''
  return {
    capInput: String(team.slotPct),
    floorInput: initialFloor,
    saving: false,
    error: null,
    savedAt: null,
  }
}

function buildRowMap(teams: ReadonlyArray<Team>): RowMap {
  const m: RowMap = new Map()
  for (const t of teams) m.set(t.id, initialRowFor(t))
  return m
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function isCustomCap(pct: number): boolean {
  return pct !== 30
}

// ─── shared save error helper ─────────────────────────────────────────────────

function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return `Save failed (${err.status})`
  if (err instanceof Error) return err.message
  return 'Save failed'
}

// ─── component ────────────────────────────────────────────────────────────────

const SAVED_CHIP_DURATION_MS = 1600

export function HrCoverageThresholdsPage(): JSX.Element {
  const queryClient = useQueryClient()
  const { notify } = useToast()

  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: listTeams,
  })

  // When the capacity-change-request feature is on, this page is READ-ONLY —
  // capacity is changed only through the Change requests tab (recorded with
  // evidence + effectivity). We still load the active overrides so a
  // temporarily-bumped team shows an info icon with its window.
  const readOnly = flags.capacityChangeRequests
  const capacityRequestsQuery = useQuery({
    queryKey: ['capacity-requests'],
    queryFn: () => listCapacityRequests(),
    enabled: readOnly,
    staleTime: 10_000,
  })
  const capacityRequests = capacityRequestsQuery.data ?? []

  // Per-row controlled state. Seeded from teamsQuery.data via effect so
  // the seed reaches React's state queue before any user input. Rows
  // missing from the map (e.g. after HMR) are filled in lazily by the
  // change handlers — see `withRow` below.
  const [rowMap, setRowMap] = useState<RowMap>(new Map())

  // A capacity change awaiting a justification (out-of-system process). When
  // `flags.outOfSystemLeave` is on, cap/floor edits open a reason prompt before
  // committing so the audit trail captures who/why/when.
  const [pendingChange, setPendingChange] = useState<{
    teamId: string
    teamName: string
    kind: 'cap' | 'floor'
    value: number | null
    label: string
  } | null>(null)
  const [reasonDraft, setReasonDraft] = useState<string>('')

  useEffect(() => {
    if (!teamsQuery.data) return
    setRowMap((prev) => {
      // Preserve any in-flight edits the user already made; only insert
      // rows that aren't in the map yet.
      const next = new Map(prev)
      let changed = false
      for (const t of teamsQuery.data) {
        if (!next.has(t.id)) {
          next.set(t.id, initialRowFor(t))
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [teamsQuery.data])

  // Auto-clear the per-row "Saved" chip 1.6s after each successful save.
  // Re-runs whenever rowMap changes; only fires for rows whose savedAt
  // is non-null and still recent.
  useEffect(() => {
    const now = Date.now()
    const timers: ReturnType<typeof setTimeout>[] = []
    rowMap.forEach((row, teamId) => {
      if (row.savedAt == null) return
      const elapsed = now - row.savedAt
      const remaining = Math.max(0, SAVED_CHIP_DURATION_MS - elapsed)
      timers.push(
        setTimeout(() => {
          setRowMap((prev) => {
            const next = new Map(prev)
            const existing = next.get(teamId)
            if (!existing || existing.savedAt == null) return prev
            next.set(teamId, { ...existing, savedAt: null })
            return next
          })
        }, remaining),
      )
    })
    return () => {
      for (const t of timers) clearTimeout(t)
    }
  }, [rowMap])

  // ── cap mutation (existing) ──────────────────────────────────────────────

  const capMutation = useMutation({
    mutationFn: ({
      teamId,
      slotPct,
      reason,
    }: {
      teamId: string
      slotPct: number
      reason?: string
    }) => updateTeamCoverage(teamId, slotPct, reason),
    onSuccess: (updated) => {
      queryClient.setQueryData<Team[]>(['teams'], (prev) =>
        prev ? prev.map((t) => (t.id === updated.id ? updated : t)) : prev,
      )
      patchRow(updated.id, {
        capInput: String(updated.slotPct),
        saving: false,
        error: null,
        savedAt: Date.now(),
      })
      notify(`Saved · ${updated.name} cap → ${updated.slotPct}%`, 'success')
    },
    onError: (err, { teamId }) => {
      patchRow(teamId, { saving: false, error: apiErrorMessage(err) })
      notify(`Save failed — ${apiErrorMessage(err)}`, 'error')
    },
  })

  // ── threshold mutation (floor / criticalRoles / overrideApprover) ────────

  const thresholdMutation = useMutation({
    mutationFn: ({
      teamId,
      patch,
    }: {
      teamId: string
      patch: Parameters<typeof updateTeamThresholds>[1]
    }) => updateTeamThresholds(teamId, patch),
    onSuccess: (updated) => {
      queryClient.setQueryData<Team[]>(['teams'], (prev) =>
        prev ? prev.map((t) => (t.id === updated.id ? updated : t)) : prev,
      )
      patchRow(updated.id, {
        floorInput: updated.floor != null ? String(updated.floor) : '',
        saving: false,
        error: null,
        savedAt: Date.now(),
      })
      const floorMsg =
        updated.floor != null ? `floor → ${updated.floor}` : 'floor cleared'
      notify(`Saved · ${updated.name} ${floorMsg}`, 'success')
    },
    onError: (err, { teamId }) => {
      patchRow(teamId, { saving: false, error: apiErrorMessage(err) })
      notify(`Save failed — ${apiErrorMessage(err)}`, 'error')
    },
  })

  // ── cap handlers ─────────────────────────────────────────────────────────

  // Patch a single row, inserting a fresh row from the team record if
  // missing. Keeps user input from being dropped when the seeding effect
  // hasn't run yet (initial paint, HMR refresh, refetch).
  const patchRow = useCallback(
    (teamId: string, patchFields: Partial<RowState>): void => {
      setRowMap((prev) => {
        const next = new Map(prev)
        const existing = next.get(teamId)
        if (existing) {
          next.set(teamId, { ...existing, ...patchFields })
          return next
        }
        const team = teamsQuery.data?.find((t) => t.id === teamId)
        const base = team
          ? initialRowFor(team)
          : { capInput: '', floorInput: '', saving: false, error: null, savedAt: null }
        next.set(teamId, { ...base, ...patchFields })
        return next
      })
    },
    [teamsQuery.data],
  )

  const onCapChange = useCallback(
    (teamId: string, value: string): void => {
      patchRow(teamId, { capInput: value, error: null })
    },
    [patchRow],
  )

  const onCapBlur = useCallback(
    (teamId: string): void => {
      const row = rowMap.get(teamId)
      if (!row) return

      const parsed = parseInt(row.capInput, 10)
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
        patchRow(teamId, { error: 'Enter a value between 0 and 100' })
        return
      }

      const team = teamsQuery.data?.find((t) => t.id === teamId)
      if (team && parsed === team.slotPct) return

      if (flags.outOfSystemLeave) {
        // Require a justification before committing the capacity change.
        setPendingChange({
          teamId,
          teamName: team?.name ?? 'this team',
          kind: 'cap',
          value: parsed,
          label: `absence cap → ${parsed}%`,
        })
        setReasonDraft('')
        return
      }

      patchRow(teamId, { saving: true, error: null })
      capMutation.mutate({ teamId, slotPct: parsed })
    },
    [rowMap, teamsQuery.data, capMutation, patchRow],
  )

  // ── floor handlers ────────────────────────────────────────────────────────

  const onFloorChange = useCallback(
    (teamId: string, value: string): void => {
      patchRow(teamId, { floorInput: value, error: null })
    },
    [patchRow],
  )

  const onFloorBlur = useCallback(
    (teamId: string): void => {
      const row = rowMap.get(teamId)
      if (!row) return

      const team = teamsQuery.data?.find((t) => t.id === teamId)
      if (!team) return

      const isSmallTeam = team.members <= 3
      const minFloor = isSmallTeam ? 1 : 0
      const maxFloor = team.members

      const raw = row.floorInput.trim()
      const parsed = raw === '' ? null : parseInt(raw, 10)

      if (raw === '' && isSmallTeam) {
        patchRow(teamId, {
          error: 'Teams of 3 or fewer enforce a minimum floor of 1',
        })
        return
      }

      if (parsed != null && (Number.isNaN(parsed) || parsed < minFloor)) {
        patchRow(teamId, { error: `Floor must be at least ${minFloor}` })
        return
      }

      if (parsed != null && parsed > maxFloor) {
        patchRow(teamId, {
          error: `Floor can't exceed team headcount (${maxFloor})`,
        })
        return
      }

      const currentFloor = team.floor ?? null
      if (parsed === currentFloor) return

      if (flags.outOfSystemLeave) {
        setPendingChange({
          teamId,
          teamName: team.name,
          kind: 'floor',
          value: parsed,
          label: parsed != null ? `min-present floor → ${parsed}` : 'min-present floor cleared',
        })
        setReasonDraft('')
        return
      }

      patchRow(teamId, { saving: true, error: null })
      thresholdMutation.mutate({ teamId, patch: { floor: parsed } })
    },
    [rowMap, teamsQuery.data, thresholdMutation, patchRow],
  )

  // ── capacity-change reason prompt (outOfSystemLeave) ─────────────────────
  const confirmPendingChange = useCallback((): void => {
    if (pendingChange == null) return
    const reason = reasonDraft.trim()
    if (reason.length === 0) return
    const { teamId, kind, value } = pendingChange
    patchRow(teamId, { saving: true, error: null })
    if (kind === 'cap' && value != null) {
      capMutation.mutate({ teamId, slotPct: value, reason })
    } else {
      thresholdMutation.mutate({ teamId, patch: { floor: value, reason } })
    }
    setPendingChange(null)
    setReasonDraft('')
  }, [pendingChange, reasonDraft, capMutation, thresholdMutation, patchRow])

  const cancelPendingChange = useCallback((): void => {
    if (pendingChange == null) return
    // Revert the input to the team's persisted value so the UI stays truthful.
    const team = teamsQuery.data?.find((t) => t.id === pendingChange.teamId)
    if (team) {
      if (pendingChange.kind === 'cap') {
        patchRow(pendingChange.teamId, { capInput: String(team.slotPct), error: null })
      } else {
        patchRow(pendingChange.teamId, {
          floorInput: team.floor != null ? String(team.floor) : '',
          error: null,
        })
      }
    }
    setPendingChange(null)
    setReasonDraft('')
  }, [pendingChange, teamsQuery.data, patchRow])

  // ── render ────────────────────────────────────────────────────────────────

  const teams = teamsQuery.data ?? []
  const hasAnyError = [...rowMap.values()].some((r) => r.error !== null)

  const subtitle =
    'Per-team capacity rules. Standard cap is 30%; override per team where ops demands it.'

  return (
    <HrAccessGuard>
      {/* No inner `.page` wrapper — the Coverage hub already provides it, so
          the table aligns left with the Change requests / History tabs. Shared
          900px max-width ceiling across the tabs; the wider table fills it,
          while the narrower form / history sit left-aligned within it. */}
      <div style={{ maxWidth: 900 }}>
      <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 20, lineHeight: 1.5 }}>
        {subtitle}
      </p>

      {teamsQuery.isError ? (
        <div
          role="alert"
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--danger-line)',
            fontSize: 13,
          }}
        >
          Failed to load teams. Refresh the page to try again.
        </div>
      ) : null}

      {hasAnyError ? (
        <div
          role="alert"
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--danger-line)',
            fontSize: 13,
          }}
        >
          One or more rows have validation errors. Correct them before saving.
        </div>
      ) : null}

      <Card padding={0} style={{ overflow: 'hidden', marginBottom: 16 }}>
        <table
          data-testid="coverage-thresholds-table"
          style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
        >
          <colgroup>
            <col style={{ width: '34%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '24%' }} />
          </colgroup>
          <thead>
            <tr
              style={{
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-2)',
              }}
            >
              {(
                [
                  'Team',
                  'Headcount',
                  'Absence cap',
                  'Min present (floor)',
                ] as const
              ).map((h) => (
                <th
                  key={h}
                  style={{
                    padding: '10px 14px',
                    textAlign: 'left',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-3)',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teamsQuery.isLoading ? (
              <tr>
                <td colSpan={4} style={{ padding: 32, textAlign: 'center' }}>
                  <ThinkingDots label="Loading teams" />
                </td>
              </tr>
            ) : teams.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}
                >
                  No teams found.
                </td>
              </tr>
            ) : (
              teams.map((team, i) => {
                const row = rowMap.get(team.id)
                const capValue = row?.capInput ?? String(team.slotPct)
                const floorValue = row?.floorInput ?? (team.floor != null ? String(team.floor) : '')
                const isSaving = row?.saving ?? false
                const rowError = row?.error ?? null
                const parsedCap = parseInt(capValue, 10)
                const isCustom = !Number.isNaN(parsedCap) && isCustomCap(parsedCap)
                // A temporary override active TODAY for this team (read-only mode).
                const override = readOnly ? activeOverrideFor(team.id, capacityRequests) : null
                // Small-team rule: teams of 3 or fewer always enforce
                // a 1-slot floor. The floor is locked at 1 — HR can't unset
                // it from the UI.
                const isSmallTeam = team.members <= 3

                return (
                  <tr
                    key={team.id}
                    style={{
                      borderBottom: i < teams.length - 1 ? '1px solid var(--border)' : undefined,
                      background: isSaving ? 'var(--surface-2)' : undefined,
                      transition: 'background 0.15s',
                      animation:
                        'lms-fade-up var(--motion-duration-slow) var(--motion-ease-out) both',
                      animationDelay: `${Math.min(i, 8) * 20}ms`,
                    }}
                  >
                    {/* Team name */}
                    <td
                      style={{
                        padding: '12px 14px',
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'var(--text)',
                      }}
                    >
                      {team.name}
                    </td>

                    {/* Headcount */}
                    <td
                      style={{
                        padding: '12px 14px',
                        fontSize: 14,
                        fontVariantNumeric: 'tabular-nums',
                        color: 'var(--text-2)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {team.members}
                    </td>

                    {/* Absence cap — read-only display (+ override info icon)
                        when the capacity-request feature is on; editable input
                        wired to PATCH otherwise. */}
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {readOnly ? (
                          <>
                            <span
                              style={{
                                fontSize: 13,
                                fontFamily: 'var(--font-mono)',
                                color:
                                  override && override.newSlotPct != null
                                    ? 'var(--text-3)'
                                    : 'var(--text)',
                              }}
                            >
                              {team.slotPct}%
                            </span>
                            {override && override.newSlotPct != null ? (
                              <Tooltip
                                content={`Temporary override, active ${override.effectiveFrom} → ${override.effectiveTo}`}
                              >
                                <span
                                  data-testid={`coverage-override-${team.id}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 13,
                                    fontFamily: 'var(--font-mono)',
                                    fontWeight: 600,
                                    color: 'var(--primary)',
                                    cursor: 'help',
                                  }}
                                >
                                  → {override.newSlotPct}%
                                  <span aria-hidden style={{ opacity: 0.7, fontSize: 12 }}>
                                    ⏱
                                  </span>
                                </span>
                              </Tooltip>
                            ) : null}
                          </>
                        ) : (
                          <input
                            type="number"
                            min={0}
                            max={100}
                            data-testid={`coverage-cap-${team.id}`}
                            value={capValue}
                            disabled={isSaving}
                            onChange={(e) => onCapChange(team.id, e.target.value)}
                            onBlur={() => onCapBlur(team.id)}
                            aria-label={`Absence cap for ${team.name}`}
                            style={{
                              width: 70,
                              padding: '6px 8px',
                              borderRadius: 8,
                              border: rowError
                                ? '1.5px solid var(--danger-line)'
                                : '1px solid var(--border)',
                              background: 'var(--surface)',
                              color: 'var(--text)',
                              fontSize: 13,
                              fontFamily: 'var(--font-mono)',
                              opacity: isSaving ? 0.6 : 1,
                            }}
                          />
                        )}
                        {!readOnly ? (
                          <span style={{ fontSize: 13, color: 'var(--text-3)' }}>%</span>
                        ) : null}
                        {!readOnly && isCustom && !rowError ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 7px',
                              borderRadius: 6,
                              fontSize: 10,
                              fontWeight: 600,
                              background: 'var(--warning-bg)',
                              color: 'var(--warning-fg)',
                              border: '1px solid var(--warning-line)',
                            }}
                          >
                            custom
                          </span>
                        ) : null}
                        {isSaving ? (
                          <span
                            style={{ fontSize: 11, color: 'var(--text-3)' }}
                            aria-live="polite"
                          >
                            Saving…
                          </span>
                        ) : null}
                        {!isSaving && row?.savedAt != null ? (
                          <span
                            data-testid={`coverage-saved-${team.id}`}
                            aria-live="polite"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                              fontSize: 11,
                              fontWeight: 600,
                              color: 'var(--success)',
                              animation:
                                'lms-fade-in var(--motion-duration-quick) var(--motion-ease-out) both',
                            }}
                          >
                            ✓ Saved
                          </span>
                        ) : null}
                        {rowError ? (
                          <span role="alert" style={{ fontSize: 11, color: 'var(--danger-fg)' }}>
                            {rowError}
                          </span>
                        ) : null}
                      </div>
                    </td>

                    {/* Min present floor — editable for every team. Small
                        teams (≤ 3) pre-fill with 1 and can't be cleared
                        below 1 (small-team rule). Max is the team's
                        headcount — you can't require more present than exist. */}
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {readOnly ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              minWidth: 70,
                              fontSize: 13,
                              fontFamily: 'var(--font-mono)',
                              color: team.floor != null ? 'var(--text)' : 'var(--text-3)',
                            }}
                          >
                            {team.floor != null ? team.floor : '—'}
                            {override && override.newFloor != null ? (
                              <Tooltip
                                content={`Temporary override, active ${override.effectiveFrom} → ${override.effectiveTo}`}
                              >
                                <span style={{ color: 'var(--primary)', fontWeight: 600, cursor: 'help' }}>
                                  → {override.newFloor}
                                </span>
                              </Tooltip>
                            ) : null}
                          </span>
                        ) : (
                          <input
                            type="number"
                            min={isSmallTeam ? 1 : 0}
                            max={team.members}
                            data-testid={`coverage-floor-${team.id}`}
                            value={floorValue}
                            disabled={isSaving}
                            onChange={(e) => onFloorChange(team.id, e.target.value)}
                            onBlur={() => onFloorBlur(team.id)}
                            placeholder={isSmallTeam ? '1' : '—'}
                            aria-label={`Min present floor for ${team.name}`}
                            style={{
                              width: 70,
                              padding: '6px 8px',
                              borderRadius: 8,
                              border: rowError
                                ? '1.5px solid var(--danger-line)'
                                : '1px solid var(--border)',
                              background: 'var(--surface)',
                              color: 'var(--text)',
                              fontSize: 13,
                              fontFamily: 'var(--font-mono)',
                              opacity: isSaving ? 0.6 : 1,
                            }}
                          />
                        )}
                        <span
                          style={{
                            fontSize: 11,
                            color: 'var(--text-3)',
                            fontFamily: 'var(--font-mono)',
                          }}
                          title={
                            isSmallTeam
                              ? 'Teams of 3 or fewer always enforce a 1-slot floor. Max = headcount.'
                              : `Max = team headcount (${team.members}).`
                          }
                        >
                          / {team.members}
                        </span>
                        {isSaving ? (
                          <span
                            style={{ fontSize: 11, color: 'var(--text-3)' }}
                            aria-live="polite"
                          >
                            Saving…
                          </span>
                        ) : null}
                        {!isSaving && row?.savedAt != null ? (
                          <span
                            aria-live="polite"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                              fontSize: 11,
                              fontWeight: 600,
                              color: 'var(--success)',
                              animation:
                                'lms-fade-in var(--motion-duration-quick) var(--motion-ease-out) both',
                            }}
                          >
                            ✓ Saved
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </Card>

      {/* Info cards — mirrors prototype's two-column rule explainer */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginTop: 8,
        }}
      >
        <Card padding={20}>
          <h3
            style={{
              margin: '0 0 8px',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text)',
            }}
          >
            How the cap is computed
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: 12.5,
              color: 'var(--text-2)',
              lineHeight: 1.6,
            }}
          >
            Max concurrent leave = 30% of headcount, rounded down. Hard-blocked
            at submission once reached.{' '}
            For an 8-person team:{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                color: 'var(--text)',
                background: 'var(--surface-2)',
                padding: '1px 4px',
                borderRadius: 4,
              }}
            >
              floor(8 × 0.30) = 2 absent slots
            </code>
          </p>
        </Card>

        <Card padding={20}>
          <h3
            style={{
              margin: '0 0 8px',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text)',
            }}
          >
            Small-team rule
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: 12.5,
              color: 'var(--text-2)',
              lineHeight: 1.6,
            }}
          >
            Teams of 3 or fewer: 1 slot floor is always enforced. Second
            concurrent request proceeds with a soft warning to the manager.
          </p>
        </Card>
      </div>
      </div>

      <Modal
        open={pendingChange != null}
        onClose={cancelPendingChange}
        title="Reason for capacity change"
        {...(pendingChange
          ? { subtitle: `${pendingChange.teamName} · ${pendingChange.label}` }
          : {})}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="tertiary" onClick={cancelPendingChange}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={confirmPendingChange}
              disabled={reasonDraft.trim().length === 0}
              data-testid="coverage-reason-confirm"
            >
              Save change
            </Button>
          </div>
        }
      >
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12, lineHeight: 1.5 }}>
          Capacity changes are audited. Record why you’re opening capacity (e.g.
          an out-of-system leave was approved for this team).
        </p>
        <textarea
          value={reasonDraft}
          onChange={(e) => setReasonDraft(e.target.value)}
          rows={3}
          autoFocus
          placeholder="e.g. Opening one slot — unpaid leave approved offline for A. Rahman."
          data-testid="coverage-reason-input"
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: 13,
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontFamily: 'var(--font-sans)',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
      </Modal>
    </HrAccessGuard>
  )
}
